import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Mic, MicOff, Video, VideoOff, Pause, Play, StopCircle, Circle, Share2, X, ArrowRight, Radio, Sparkles,
} from "lucide-react";
import api from "../lib/api";
import { getUserId } from "../lib/user";
import { createPeer, openSignal } from "../lib/webrtc";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import AddToCalendar from "../components/AddToCalendar";
import { createSoftLightPipeline, readSoftLightLevel, writeSoftLightLevel, nextSoftLightLevel, SOFT_LEVELS } from "../lib/softLight";
import { isKickoff, periodNoun, cadenceLabel } from "../lib/cadence";
import {
  startSessionRecording, uploadRecording, savePendingRecording, clearPendingRecording,
} from "../lib/recording";
import { setBackgroundUpload } from "../lib/backgroundUpload";
import { startLiveTranscription } from "../lib/liveTranscribe";
import { GoalReviewPanel } from "../components/GoalReviewPanel";
import RenameMeeting from "../components/RenameMeeting";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

function formatTime(s) {
  const sec = Math.max(0, Math.floor(s));
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const r = (sec % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
}

export default function Meeting() {
  const { id } = useParams();
  const nav = useNavigate();

  const [meeting, setMeeting] = useState(null);
  const [carryover, setCarryover] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [notes, setNotes] = useState({});
  const [carry, setCarry] = useState({ items: [], source: null });
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [endOpen, setEndOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const leftRef = useRef(false);
  const [liveOn, setLiveOn] = useState(false);
  const [partnerConnected, setPartnerConnected] = useState(false);
  const [softLevel, setSoftLevel] = useState(readSoftLightLevel);
  const [renamingTitle, setRenamingTitle] = useState(false);

  const videoRef = useRef(null);
  const partnerVideoRef = useRef(null);
  const streamRef = useRef(null);
  const rawStreamRef = useRef(null);
  const softRef = useRef(null);
  const recorderRef = useRef(null);
  const liveRef = useRef(null);
  const tickRef = useRef(null);
  const elapsedRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const guestIdRef = useRef(null);
  const iceQueueRef = useRef([]);

  // Load meeting
  useEffect(() => {
    let cancelled = false;
    api.get(`/meetings/${id}`).then(async (r) => {
      if (cancelled) return;
      setMeeting(r.data);
      setNotes(r.data.notes || {});
      const agenda = r.data.agenda || [];
      setSecondsRemaining((agenda[0]?.minutes || 5) * 60);

      const carryIds = r.data.carryover_action_item_ids || [];
      if (carryIds.length) {
        try {
          const all = await api.get("/action-items");
          setCarryover(all.data.filter((it) => carryIds.includes(it.id) && it.status === "pending"));
        } catch (e) { console.debug("carryover fetch failed", e); }
      }

      if (isKickoff(r.data.cadence)) {
        try {
          const cf = await api.get(`/meetings/${id}/carry-forward`);
          if (!cancelled) setCarry({ items: cf.data.items || [], source: cf.data.source || null });
        } catch (e) { console.debug("carry-forward fetch failed", e); }
      }
    }).catch(() => {
      toast.error("Session not found");
      nav("/");
    });
    return () => { cancelled = true; };
  }, [id, nav]);

  // Set meeting active on first load — but never for a session that is still
  // scheduled in the future. Those stay 'scheduled' so the user keeps their
  // calendar / reschedule affordances until they actually hold it.
  useEffect(() => {
    if (!meeting || meeting.status !== "scheduled") return;
    const sched = meeting.scheduled_for ? new Date(meeting.scheduled_for).getTime() : 0;
    if (sched && sched > Date.now() + 2 * 60 * 1000) return;
    api.patch(`/meetings/${id}`, { status: "active" }).catch(() => {});
  }, [meeting, id]);

  // Get media stream
  const beginRecording = () => {
    if (recorderRef.current) return true;
    const handle = startSessionRecording({
      processedStream: streamRef.current,
      rawStream: rawStreamRef.current,
    });
    if (!handle) {
      toast.error("This browser can't record — your notes will still be saved.", { duration: 6000 });
      return false;
    }
    recorderRef.current = handle;
    setIsRecording(true);
    // Transcribe as we go, so adjourning doesn't start a long wait.
    try {
      liveRef.current = startLiveTranscription({
        stream: rawStreamRef.current || streamRef.current,
        meetingId: id,
      });
    } catch (e) {
      console.warn("Live transcription unavailable", e);
    }
    if (handle.kind === "audio") {
      toast.message("Recording audio only", {
        description: "This browser can't record video — the audio still becomes your transcript and minutes.",
        duration: 5000,
      });
    }
    return true;
  };

  useEffect(() => {
    let active = true;
    async function setup() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 1280, height: 720 },
          audio: true,
        });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        rawStreamRef.current = stream;
        // Soft-light runs before anything else consumes the feed, so the
        // preview, the recording and the partner all see the same picture.
        const pipeline = createSoftLightPipeline(stream, { level: readSoftLightLevel() });
        softRef.current = pipeline;
        streamRef.current = pipeline ? pipeline.stream : stream;
        if (videoRef.current) {
          videoRef.current.srcObject = streamRef.current;
        }
        // Record from the moment we have a feed. Waiting for a button press
        // was losing whole sessions when the user never pressed it.
        beginRecording();
      } catch (e) {
        console.error(e);
        toast.error("Camera/mic permission denied — recording disabled.");
      }
    }
    setup();
    return () => {
      active = false;
      if (liveRef.current) {
        liveRef.current.stop();
        liveRef.current = null;
      }
      if (softRef.current) {
        softRef.current.stop();
        softRef.current = null;
      }
      if (rawStreamRef.current) {
        rawStreamRef.current.getTracks().forEach((t) => t.stop());
        rawStreamRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Timer tick — counts down current section, counts up total elapsed
  useEffect(() => {
    if (!running) return;
    tickRef.current = setInterval(() => {
      setSecondsRemaining((s) => Math.max(0, s - 1));
    }, 1000);
    elapsedRef.current = setInterval(() => {
      setElapsed((e) => e + 1);
    }, 1000);
    return () => {
      clearInterval(tickRef.current);
      clearInterval(elapsedRef.current);
    };
  }, [running]);

  // Auto-advance when section timer hits zero
  useEffect(() => {
    if (running && secondsRemaining === 0 && meeting) {
      const agenda = meeting.agenda || [];
      if (activeIdx < agenda.length - 1) {
        toast(`Time’s up on "${agenda[activeIdx].title}"`, { description: "Moving to next section." });
        const next = activeIdx + 1;
        setActiveIdx(next);
        setSecondsRemaining((agenda[next]?.minutes || 5) * 60);
      } else {
        setRunning(false);
        toast("Agenda complete. End the session when ready.");
      }
    }
    // `meeting` and `activeIdx` are read inside — include them so React
    // doesn't hold onto a stale closure if the meeting doc reloads or the
    // user hops sections manually. The `secondsRemaining === 0` guard keeps
    // this cheap despite firing on every counter tick.
  }, [secondsRemaining, running, meeting, activeIdx]);

  const appendToCommitments = (lines) => {
    if (!lines.length) return;
    const existing = (notes.commitments || "").trimEnd();
    const block = lines.map((t) => `Carried over: ${t}`).join("\n");
    updateNote("commitments", existing ? `${existing}\n${block}` : block);
    const target = agenda.findIndex((s) => s.key === "commitments");
    if (target >= 0) selectSection(target);
    toast.success(lines.length === 1 ? "Carried into your commitments." : `${lines.length} promises carried over.`);
  };

  const carryOver = (i) => {
    const item = carry.items[i];
    if (!item) return;
    appendToCommitments([item.title]);
    setCarry((prev) => ({ ...prev, items: prev.items.filter((_, idx) => idx !== i) }));
  };

  const carryAll = () => {
    appendToCommitments(carry.items.map((it) => it.title));
    setCarry((prev) => ({ ...prev, items: [] }));
  };

  const handleStart = () => {
    if (!streamRef.current) {
      toast.error("No camera/mic stream available");
      return;
    }
    setRunning(true);
    if (!recorderRef.current) {
      const started = beginRecording();
      if (started) toast.success("Session called to order — recording.");
    } else {
      toast.success("Session called to order — recording.");
    }
  };

  const togglePause = () => {
    if (!recorderRef.current) {
      setRunning((r) => !r);
      return;
    }
    if (paused) {
      recorderRef.current.resume();
      setPaused(false);
      setRunning(true);
    } else {
      recorderRef.current.pause();
      setPaused(true);
      setRunning(false);
    }
  };

  const toggleCam = () => {
    const src = rawStreamRef.current || streamRef.current;
    if (!src) return;
    const next = !camOn;
    // Cut the camera itself so the hardware light goes out; the canvas keeps
    // publishing (black) frames so the peer connection stays up.
    src.getVideoTracks().forEach((t) => (t.enabled = next));
    setCamOn(next);
  };

  // One button cycles Off → Soft → Softer, so it stays out of the way mid-session.
  const cycleSoftLight = () => {
    const next = nextSoftLightLevel(softLevel);
    setSoftLevel(next);
    writeSoftLightLevel(next);
    if (softRef.current) softRef.current.setLevel(next);
    toast.success(`Soft light · ${SOFT_LEVELS[next].label}`, { duration: 1400 });
  };

  const toggleMic = () => {
    if (!streamRef.current) return;
    streamRef.current.getAudioTracks().forEach((t) => (t.enabled = !t.enabled));
    setMicOn((v) => !v);
  };

  const selectSection = (i) => {
    setActiveIdx(i);
    const sec = meeting.agenda[i];
    setSecondsRemaining((sec.minutes || 5) * 60);
  };

  const updateNote = (key, value) => {
    setNotes((prev) => ({ ...prev, [key]: value }));
  };

  const saveNotes = async () => {
    try {
      await api.patch(`/meetings/${id}`, { notes });
    } catch (e) {
      console.debug("notes autosave failed", e);
    }
  };

  // Save notes periodically
  useEffect(() => {
    if (!meeting) return;
    const t = setTimeout(saveNotes, 1200);
    return () => clearTimeout(t);
  }, [notes]);

  // Leaves the saving screen while the upload keeps running in the background.
  const leaveWhileUploading = () => {
    leftRef.current = true;
    setBackgroundUpload({ meetingId: id, title: meeting?.title || "session", progress: (uploadPct || 0) / 100 });
    setUploadPct(null);
    nav("/meetings");
  };

  const handleEnd = async () => {
    setEndOpen(false);
    setSaving(true);
    setRunning(false);

    let recordingBlob = null;
    let mime = null;
    let ext = null;
    if (liveRef.current) {
      // Flush the last clip so the transcript is complete before drafting.
      try { await liveRef.current.stop(); } catch (e) { console.warn("Live transcript flush failed", e); }
      liveRef.current = null;
    }
    if (recorderRef.current) {
      try {
        mime = recorderRef.current.mime;
        ext = recorderRef.current.ext;
        recordingBlob = await recorderRef.current.stop();
      } catch (e) { console.error("Stopping the recorder failed", e); }
      recorderRef.current = null;
      setIsRecording(false);
    }

    // Park the blob on this device FIRST. If the upload or the tab dies, the
    // next app load finishes the job instead of losing the session.
    if (recordingBlob && recordingBlob.size > 0) {
      await savePendingRecording(id, recordingBlob, mime, ext);
    }

    try {
      // Mark the session completed — small payload, finishes instantly.
      await api.patch(`/meetings/${id}`, { notes, status: "completed", duration_seconds: elapsed });

      if (recordingBlob && recordingBlob.size > 0) {
        setUploadPct(0);
        try {
          await uploadRecording(id, recordingBlob, {
            mime,
            ext,
            onProgress: (p) => {
              setUploadPct(Math.round(p * 100));
              // Mirror progress into the app-wide store so the banner keeps
              // reporting if the user leaves this screen mid-upload.
              if (leftRef.current) {
                setBackgroundUpload({ meetingId: id, title: meeting?.title || "session", progress: p });
              }
            },
          });
          await clearPendingRecording(id);
        } catch (e) {
          console.error("Recording upload failed", e);
          toast.error("Recording didn't finish uploading — it's saved on this device and we'll retry automatically.", { duration: 8000 });
        }
        setBackgroundUpload(null);
        setUploadPct(null);
      } else if (!recordingBlob) {
        toast.message("No recording was captured", {
          description: "Your notes and minutes are saved, but there's no audio for a transcript.",
          duration: 7000,
        });
      }

      toast.success("Session adjourned. Minutes are drafting — open the session archive to see them when ready.", { duration: 6000 });
      nav("/meetings");

      // Minutes draft server-side; the Minutes page polls for them.
      api.post(`/meetings/${id}/minutes/start`).catch((e) => {
        console.debug("Background minutes generation deferred to page-load auto-trigger", e?.response?.data || e);
      });
    } catch (e) {
      console.error(e);
      toast.error("Could not finalize session");
    } finally {
      setBackgroundUpload(null);
      setSaving(false);
    }
  };

  const createInvite = async () => {
    try {
      const res = await api.post("/invitations", { meeting_id: id, invitee_label: "Partner" });
      const link = `${window.location.origin}/invite/${res.data.token}`;
      setInviteLink(link);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && (detail.code === "plan_required" || detail.code === "invite_cap")) {
        toast.error(detail.message, {
          action: { label: "Upgrade", onClick: () => (window.location.href = "/pricing") },
          duration: 8000,
        });
      } else {
        toast.error("Could not create invite");
      }
    }
  };

  // ---- WebRTC live mode (host) ----
  const stopLive = () => {
    if (wsRef.current) { try { wsRef.current.close(); } catch (e) { console.debug("ws close failed", e); } wsRef.current = null; }
    if (pcRef.current) { try { pcRef.current.close(); } catch (e) { console.debug("pc close failed", e); } pcRef.current = null; }
    setPartnerConnected(false);
    setLiveOn(false);
  };

  const startLive = () => {
    if (!streamRef.current) {
      toast.error("No camera/mic stream — cannot start live call");
      return;
    }
    setLiveOn(true);
    iceQueueRef.current = [];

    const pc = createPeer({
      stream: streamRef.current,
      onTrack: (s) => {
        if (partnerVideoRef.current) partnerVideoRef.current.srcObject = s;
        setPartnerConnected(true);
      },
      onIce: (cand) => {
        if (wsRef.current?.readyState === 1 && guestIdRef.current) {
          wsRef.current.send(JSON.stringify({ type: "ice", to: guestIdRef.current, candidate: cand }));
        }
      },
    });
    pcRef.current = pc;

    const ws = openSignal({
      meetingId: id,
      userId: getUserId(),
      role: "host",
      onMessage: async (msg) => {
        if (msg.type === "ready") {
          guestIdRef.current = msg.from;
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          ws.send(JSON.stringify({ type: "offer", to: msg.from, sdp: pc.localDescription }));
        } else if (msg.type === "answer") {
          await pc.setRemoteDescription(msg.sdp);
          for (const c of iceQueueRef.current) await pc.addIceCandidate(c);
          iceQueueRef.current = [];
        } else if (msg.type === "ice") {
          const cand = new RTCIceCandidate(msg.candidate);
          if (pc.remoteDescription) await pc.addIceCandidate(cand);
          else iceQueueRef.current.push(cand);
        } else if (msg.type === "peer-left") {
          setPartnerConnected(false);
          if (partnerVideoRef.current) partnerVideoRef.current.srcObject = null;
        }
      },
    });
    wsRef.current = ws;
    ws.onopen = () => toast.success("Live channel open — partners can now join.");
    ws.onerror = () => toast.error("Live channel error");
  };

  // Cleanup live on unmount
  useEffect(() => {
    return () => stopLive();
  }, []);

  if (!meeting) {
    return <div className="min-h-screen flex items-center justify-center text-[#888]">Calling session to order…</div>;
  }

  const agenda = meeting.agenda || [];
  const active = agenda[activeIdx];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#fafafa] flex flex-col" data-testid="meeting-room">
      {/* Top bar */}
      <div className="glass border-b border-white/5 px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-y-3 gap-x-3">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <Link
            to="/"
            className="h-10 w-10 -ml-2 flex items-center justify-center text-[#888] hover:text-white transition-colors shrink-0"
            aria-label="Leave session"
            data-testid="exit-meeting"
          >
            <X size={18} />
          </Link>
          <div className="min-w-0">
            <p className="label-eyebrow">In session · {cadenceLabel(meeting.cadence)}</p>
            <div className="flex items-center gap-1 min-w-0">
              {!renamingTitle && (
                <h2 className="font-serif text-base sm:text-lg leading-tight truncate">{meeting.title}</h2>
              )}
              <RenameMeeting
                meeting={meeting}
                compact
                onEditingChange={setRenamingTitle}
                onUpdated={(m) => setMeeting({ ...meeting, ...m })}
              />
            </div>
          </div>
          {/* Calendar add — only useful while the session hasn't started
              yet. Once we're recording, the user is here now; a calendar
              invite is pointless. */}
          {meeting.status === "scheduled" && !running && (
            <AddToCalendar
              meeting={meeting}
              compact
              onUpdated={(m) => setMeeting(m)}
              className="hidden sm:block"
            />
          )}
        </div>
        <div className="flex items-center gap-3 sm:gap-6 ml-auto">
          {isRecording && (
            <div className="flex items-center gap-2" data-testid="recording-indicator">
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs font-mono uppercase tracking-widest text-red-400">REC</span>
            </div>
          )}
          {liveOn && (
            <div className="flex items-center gap-2" data-testid="live-indicator">
              <span className={`h-2 w-2 rounded-full ${partnerConnected ? "bg-emerald-400" : "bg-[#c89f45]"} animate-pulse`} />
              <span className="text-xs font-mono uppercase tracking-widest text-[#c89f45]">
                {partnerConnected ? "LIVE · 2" : "LIVE · WAITING"}
              </span>
            </div>
          )}
          <div className="font-mono text-sm text-[#888]">
            <span className="text-[#c89f45]" data-testid="meeting-elapsed">{formatTime(elapsed)}</span><span className="hidden sm:inline"> elapsed</span>
          </div>
          {!liveOn ? (
            <Button
              variant="outline"
              onClick={startLive}
              className="bg-transparent border-[#c89f45] text-[#c89f45] hover:bg-[#c89f45] hover:text-[#0a0a0a] rounded-md h-10 sm:h-9 px-3"
              data-testid="go-live-button"
            >
              <Radio size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Go live</span>
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={stopLive}
              className="bg-transparent border-[#2a2a2a] text-[#fafafa] hover:bg-[#1a1a1a] hover:text-white rounded-md h-10 sm:h-9 px-3"
              data-testid="stop-live-button"
            >
              <Radio size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Stop live</span>
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => { setInviteOpen(true); if (!inviteLink) createInvite(); }}
            className="bg-transparent border-[#2a2a2a] text-[#fafafa] hover:bg-[#1a1a1a] hover:text-white rounded-md h-10 sm:h-9 px-3"
            data-testid="invite-partner-button"
          >
            <Share2 size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Invite</span>
          </Button>
        </div>
      </div>

      {/* Main split */}
      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Video + controls */}
        <div className="lg:flex-1 lg:w-[68%] p-6 lg:p-10 flex flex-col gap-6">
          <div className="relative">
          <div className="relative aspect-video rounded-md overflow-hidden bg-[#080808] border border-[#2a2a2a]" data-testid="video-frame">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              /* Mirrored for the chair only — the recording and the partner
                 feed stay the correct way round so text reads properly. */
              className="w-full h-full object-cover scale-x-[-1]"
              data-testid="self-video"
            />
            <div className="absolute top-4 left-4 glass px-3 py-1.5 rounded">
              <p className="label-eyebrow text-[#c89f45]">{active?.title}</p>
            </div>
            {liveOn && (
              <div className="absolute top-4 right-4 w-40 md:w-56 aspect-video rounded-md overflow-hidden border border-[#c89f45]/60 bg-[#0a0a0a] shadow-[0_8px_30px_rgba(0,0,0,0.6)]" data-testid="partner-tile">
                <video ref={partnerVideoRef} autoPlay playsInline className="w-full h-full object-cover" />
                {!partnerConnected && (
                  <div className="absolute inset-0 flex items-center justify-center text-center px-3">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[#888]">Waiting for partner…</p>
                  </div>
                )}
                <div className="absolute bottom-2 left-2 glass px-2 py-0.5 rounded">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#c89f45]">Partner</p>
                </div>
              </div>
            )}
            </div>
            <div className="mt-3 sm:mt-0 sm:absolute sm:bottom-4 sm:left-1/2 sm:-translate-x-1/2 sm:max-w-[calc(100%-1.5rem)] glass rounded-2xl sm:rounded-full px-2.5 sm:px-3 py-2 flex flex-wrap items-center justify-center gap-1.5 sm:gap-2">
              <button
                onClick={toggleMic}
                className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${
                  micOn ? "bg-[#1a1a1a] text-white" : "bg-red-900 text-white"
                }`}
                data-testid="toggle-mic"
              >
                {micOn ? <Mic size={16} /> : <MicOff size={16} />}
              </button>
              <button
                onClick={toggleCam}
                className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${
                  camOn ? "bg-[#1a1a1a] text-white" : "bg-red-900 text-white"
                }`}
                data-testid="toggle-cam"
              >
                {camOn ? <Video size={16} /> : <VideoOff size={16} />}
              </button>
              {!running && !paused ? (
                <button
                  onClick={handleStart}
                  className="h-10 px-4 rounded-full bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] text-sm font-medium flex items-center gap-2 transition-colors"
                  data-testid="start-meeting-button"
                >
                  <Circle size={12} fill="#0a0a0a" /> Call to order
                </button>
              ) : (
                <button
                  onClick={togglePause}
                  className="h-10 w-10 rounded-full bg-[#1a1a1a] text-white flex items-center justify-center"
                  data-testid="pause-meeting-button"
                >
                  {paused ? <Play size={16} /> : <Pause size={16} />}
                </button>
              )}
              <button
                onClick={cycleSoftLight}
                className={`h-10 rounded-full flex items-center justify-center gap-1.5 transition-colors ${
                  softLevel === "off" ? "bg-[#1a1a1a] text-[#888] w-10" : "bg-[#c89f45]/20 text-[#c89f45] px-3"
                }`}
                title={`Soft light · ${SOFT_LEVELS[softLevel].label} (tap to change)`}
                data-testid="toggle-soft-light"
                data-soft-level={softLevel}
              >
                <Sparkles size={16} />
                {softLevel !== "off" && (
                  <span className="text-[10px] font-mono uppercase tracking-widest hidden sm:inline">
                    {SOFT_LEVELS[softLevel].label}
                  </span>
                )}
              </button>
              <button
                onClick={() => setEndOpen(true)}
                className="h-10 px-4 rounded-full bg-red-900 hover:bg-red-800 text-white text-sm font-medium flex items-center gap-2 transition-colors"
                data-testid="end-meeting-button"
              >
                <StopCircle size={14} /> Adjourn
              </button>
            </div>
          </div>

          {/* Section countdown */}
          <div className="surface rounded-md p-6 flex items-center justify-between" data-testid="agenda-timer-display">
            <div>
              <p className="label-eyebrow">Section</p>
              <h3 className="font-serif text-2xl mt-2">{active?.title}</h3>
            </div>
            <div className="font-mono text-5xl md:text-6xl tracking-tighter text-[#c89f45]" data-testid="section-timer">
              {formatTime(secondsRemaining)}
            </div>
          </div>
        </div>

        {/* Right rail: agenda + notes */}
        <aside className="lg:w-[32%] border-t lg:border-t-0 lg:border-l border-[#2a2a2a] p-6 lg:p-8 flex flex-col gap-6">
          <div>
            <p className="label-eyebrow">Agenda</p>
            <ul className="mt-4 space-y-1">
              {agenda.map((s, i) => {
                const isActive = i === activeIdx;
                return (
                  <li key={s.key}>
                    <button
                      onClick={() => selectSection(i)}
                      className={`w-full text-left px-4 py-3 rounded-md transition-colors flex items-center justify-between ${
                        isActive ? "tracing-beam bg-[#121212]" : "hover:bg-[#121212]"
                      }`}
                      data-testid={`agenda-item-${s.key}`}
                    >
                      <span className="flex items-center gap-3">
                        <span className={`font-mono text-xs w-6 ${isActive ? "text-[#c89f45]" : "text-[#666]"}`}>
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className={isActive ? "text-white" : "text-[#aaa]"}>{s.title}</span>
                      </span>
                      <span className="font-mono text-[10px] text-[#666]">{s.minutes}M</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {carryover.length > 0 && (
            <div className="surface rounded-md p-4 border-[#c89f45]/30" data-testid="carryover-block">
              <p className="label-eyebrow text-[#c89f45]">Carried over from last session</p>
              <ul className="mt-3 space-y-2">
                {carryover.map((it) => (
                  <li key={it.id} className="text-xs text-[#ddd] flex items-start gap-2" data-testid={`carryover-${it.id}`}>
                    <span className="text-[#c89f45]">›</span>
                    <span>{it.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Unfinished promises from the last review — one tap to carry them
              into this kickoff's commitments. */}
          {carry.items.length > 0 && (
            <div className="mb-4 rounded-md border border-[#c89f45]/40 bg-[#c89f45]/5 p-4" data-testid="carry-forward-panel">
              <p className="label-eyebrow text-[#c89f45]">Came back unfinished</p>
              <p className="text-sm text-[#ddd] mt-1.5">
                {carry.items.length} promise{carry.items.length === 1 ? "" : "s"} from your last review. Carry any of them into this {periodNoun(meeting.cadence)}.
              </p>
              <ul className="mt-3 space-y-2">
                {carry.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`carry-item-${i}`}>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#8a8a8a] mt-1.5 shrink-0 w-14">
                      {it.verdict}
                    </span>
                    <span className="text-sm text-[#eaeaea] flex-1 min-w-0">{it.title}</span>
                    <button
                      type="button"
                      onClick={() => carryOver(i)}
                      className="shrink-0 text-xs text-[#c89f45] hover:underline min-h-[32px]"
                      data-testid={`carry-add-${i}`}
                    >
                      Carry over
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={carryAll}
                className="mt-3 h-9 px-3 rounded-md bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] text-xs font-medium transition-colors"
                data-testid="carry-add-all"
              >
                Carry all into commitments
              </button>
            </div>
          )}

          <div className="flex-1 flex flex-col min-h-0">
            {(active?.key === "goals" || active?.key === "commitments") && (
              <GoalReviewPanel
                onMention={(g) =>
                  updateNote(
                    active.key,
                    `${notes[active.key] ? notes[active.key].replace(/\s*$/, "\n") : ""}${g.title} (${g.progress}%): `
                  )
                }
              />
            )}
            <div className="flex items-center justify-between mb-3">
              <p className="label-eyebrow">Notes · {active?.title}</p>
              <span className="text-[10px] text-[#666] font-mono">AUTOSAVED</span>
            </div>
            <Textarea
              value={notes[active?.key] || ""}
              onChange={(e) => updateNote(active.key, e.target.value)}
              placeholder={`What's on the table for ${active?.title.toLowerCase()}?`}
              className="flex-1 min-h-[180px] resize-none bg-[#121212] border-[#2a2a2a] text-[#fafafa] focus:border-[#c89f45] focus:ring-0 rounded-md"
              data-testid="notes-textarea"
            />
            {activeIdx < agenda.length - 1 && (
              <Button
                onClick={() => selectSection(activeIdx + 1)}
                variant="outline"
                className="mt-3 bg-transparent border-[#2a2a2a] text-[#fafafa] hover:bg-[#1a1a1a] hover:text-white rounded-md"
                data-testid="next-section-button"
              >
                Next: {agenda[activeIdx + 1].title} <ArrowRight size={14} className="ml-2" />
              </Button>
            )}
          </div>
        </aside>
      </div>

      {/* Saving / uploading overlay — the user stays put until the recording
          is safely on the server (or parked for a retry). */}
      {uploadPct !== null && (
        <div className="fixed inset-0 z-[90] bg-[#0a0a0a]/85 backdrop-blur-sm flex items-center justify-center p-6" data-testid="upload-overlay">
          <div className="surface rounded-md p-7 w-full max-w-sm text-center">
            <p className="label-eyebrow text-[#c89f45]">Saving the record</p>
            <p className="font-serif text-2xl mt-3">Uploading your recording</p>
            <div className="h-1.5 rounded bg-[#1c1c1c] overflow-hidden mt-6">
              <div className="h-full bg-[#c89f45] transition-all" style={{ width: `${uploadPct}%` }} />
            </div>
            <p className="font-mono text-xs text-[#8a8a8a] mt-3" data-testid="upload-progress">{uploadPct}%</p>
            <p className="text-xs text-[#7a7a7a] mt-4">You can leave — we’ll finish the upload in the background.</p>
            <Button
              onClick={leaveWhileUploading}
              variant="outline"
              className="mt-5 w-full bg-transparent border-[#2a2a2a] text-[#fafafa] hover:bg-[#1a1a1a] hover:text-white rounded-md"
              data-testid="leave-while-uploading"
            >
              Leave now — finish in background
            </Button>
          </div>
        </div>
      )}

      {/* End dialog */}
      <Dialog open={endOpen} onOpenChange={setEndOpen}>
        <DialogContent className="bg-[#0a0a0a] border border-[#2a2a2a] text-[#fafafa]" data-testid="end-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Adjourn this session?</DialogTitle>
            <DialogDescription className="text-[#888]">
              We’ll save the recording, notes, and generate AI meeting minutes with an action plan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEndOpen(false)} className="bg-transparent border-[#2a2a2a] text-[#fafafa] hover:bg-[#1a1a1a] hover:text-white" data-testid="end-cancel">
              Continue session
            </Button>
            <Button onClick={handleEnd} disabled={saving} className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a]" data-testid="end-confirm">
              {saving ? "Adjourning…" : "Yes, adjourn"}
            </Button>          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-[#0a0a0a] border border-[#2a2a2a] text-[#fafafa]" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Invite a partner</DialogTitle>
            <DialogDescription className="text-[#888]">
              Share this link with your accountability partner or friend group. They’ll be added to this session.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input value={inviteLink} readOnly className="bg-[#121212] border-[#2a2a2a] focus:border-[#c89f45] font-mono text-xs" data-testid="invite-link-input" />
            <Button
              onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success("Link copied"); }}
              className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a]"
              data-testid="copy-invite-link"
            >
              Copy
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
